function to_positive_number(value) {
  const normalized_value = Number(value);
  return Number.isFinite(normalized_value) && normalized_value > 0
    ? normalized_value
    : null;
}

function get_first_positive_number(values) {
  for (const value of values) {
    const normalized_value = to_positive_number(value);
    if (normalized_value) return normalized_value;
  }

  return null;
}

function get_first_truthy_value(values, fallback = null) {
  for (const value of values) {
    if (value) return value;
  }

  return fallback;
}

function get_framework_selection_suffix(selected_frameworks_count) {
  if (selected_frameworks_count === 1) return '';
  return 's';
}

function get_framework_phase(item) {
  if (item?.assessment_phase != null) return item.assessment_phase;
  if (item?.phase != null) return item.phase;
  return '';
}

function get_framework_status(item) {
  if (item?.is_active === false) return 'Inactive';
  return 'Active';
}

export function get_binding_assessment_id(binding) {
  return get_first_positive_number([
    binding?.entity_risk_framework_assessment?.risk_framework_assessment?.id,
    binding?.entity_risk_framework_assessment?.risk_framework_assessment_id,
    binding?.risk_framework_assessment_id,
    binding?.assessment_id,
    binding?.id
  ]);
}

export function build_selected_frameworks_label({
  frameworks_loading,
  bindable_frameworks,
  selected_frameworks_count
}) {
  if (frameworks_loading) return 'Loading risk frameworks...';
  if (!bindable_frameworks.length) return 'No risk frameworks available';
  if (!selected_frameworks_count) return 'Select framework(s) to bind';
  return `${selected_frameworks_count} framework${get_framework_selection_suffix(selected_frameworks_count)} selected`;
}

export function get_bindable_frameworks(risk_frameworks, risk_bindings) {
  const bound_framework_ids = new Set(
    (Array.isArray(risk_bindings) ? risk_bindings : [])
      .map((binding) => get_binding_assessment_id(binding))
      .filter(Boolean)
  );

  return (Array.isArray(risk_frameworks) ? risk_frameworks : [])
    .filter((framework) => Boolean(to_positive_number(framework?.id)))
    .filter((framework) => !bound_framework_ids.has(Number(framework.id)));
}

function get_framework_id(item, index) {
  return get_first_positive_number([
    item?.id,
    item?.assessment_id,
    item?.risk_framework_assessment_id,
    index + 1
  ]);
}

function get_framework_name(item, index) {
  return get_first_truthy_value([
    item?.risk_assessment,
    item?.assessment_name,
    item?.name
  ], `Risk Framework ${index + 1}`);
}

export function normalize_framework(item, index = 0) {
  const framework = {
    id: get_framework_id(item, index),
    name: get_framework_name(item, index),
    description: item?.description ?? '',
    phase: get_framework_phase(item),
    status: get_framework_status(item),
  };
  return framework;
}

export function normalize_selected_framework_ids(selected_framework_ids) {
  return Array.isArray(selected_framework_ids)
    ? selected_framework_ids.map((id) => String(id)).filter(Boolean)
    : [];
}

export function create_binding_failure(assessment_id, message) {
  const result = {
    ok: false,
    assessment_id,
    message,
  };
  return result;
}

export function create_binding_success(assessment_id, response_data) {
  const response = {
    ok: true,
    assessment_id,
    response_data,
  };

  return response;
}

export function is_valid_assessment_id(assessment_id) {
  return Boolean(to_positive_number(assessment_id));
}

export function get_unique_selected_ids(selected_ids) {
  return Array.from(new Set(selected_ids));
}

export function apply_binding_result(result, successes, failures) {
  if (result.ok) {
    successes.push(result.assessment_id);
    return;
  }

  failures.push(result);
}

export function build_failure_message(failures) {
  return failures.length === 1
    ? (failures[0]?.message || 'Failed to assign risk framework.')
    : `${failures.length} risk frameworks failed to assign.`;
}

export function get_failed_selection_ids(failures) {
  return failures
    .map((failure) => String(failure.assessment_id))
    .filter(Boolean);
}

export function get_binding_title(binding) {
  return get_first_truthy_value([
    binding?.assessment_name,
    binding?.risk_assessment_name,
    binding?.entity_risk_framework_assessment?.risk_framework_assessment?.risk_assessment,
    binding?.entity_risk_framework_assessment?.assessment_name
  ], 'Risk Framework');
}
